/**
 * THE FLIGHTDECK MARK — a flight progress strip.
 *
 * A flight progress strip is the paper card an air traffic controller writes on
 * and slots into a rack, one card per aircraft, kept in order. It is the closest
 * real object to what this product is: a record card per release, in order. That
 * is why it was chosen over the three lines that were here before — those said
 * "aviation", this says "a record of what shipped".
 *
 * WHY THE OLD THREE LINES WENT. They did not fit a favicon. At 16px the drawing
 * scales to 0.42px per grid unit; the two rules sat 5 units apart (2.08px centre
 * to centre) with strokes 1.42px thick, leaving 0.66px of clear space between
 * them. Below one pixel, so on a standard-density screen they merged into one
 * bar and the mark stopped being three strokes. This one leaves 3.25px.
 *
 * ON TRIAL, NOT COMMITTED. Put here on 16 August to be lived with for a day
 * before it is kept. If it goes, the previous mark is in git history at 6572b63.
 */

/** Below this the card's written lines turn to mud, so they come off and the
 *  strip reads as an outline with a tab — which is the part carrying the idea.
 *  18px because at 16px the two lines sit 2.33px apart with 1.33px strokes,
 *  leaving 1px between them. */
const LINES_LEGIBLE_ABOVE = 18;

/**
 * The tab's amber, and the one thing to watch while this is on trial.
 *
 * styles.css says of the environment banner: "Amber is used nowhere else in the
 * interface, so this cannot be mistaken for ordinary furniture." That banner sits
 * directly above this mark on every non-live copy of the board, so for a day the
 * claim is not quite true and the two ambers are within an inch of each other.
 * If that reads badly, the fix is one line — set this to 'currentColor' and the
 * mark becomes single-value, which loses nothing at small sizes.
 *
 * Not a CSS variable on purpose: it must not shift with the theme. The tile
 * behind it is --brand-solid, which is deep petrol on light and bright teal on
 * dark, and a tab that changed colour with the ground would stop being a flag.
 */
const STRIP_TAB = '#f5a623';

export function LogoMark({ size = 13 }: { size?: number }) {
  const detailed = size >= LINES_LEGIBLE_ABOVE;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
    >
      {/* The card. Drawn on the same 24×24 grid as the icon set so the weights
          match the rest of the interface. */}
      <rect
        x="2.5"
        y="6"
        width="19"
        height="12"
        rx="1.6"
        fill="none"
        stroke="currentColor"
        strokeWidth={detailed ? 2.4 : 2.8}
      />
      {/* The tab down the left edge — how a strip is colour-flagged in a rack.
          Flush to the edge rather than inset: an inset block is 1.5px at favicon
          size and disappears, flush it is 2.3px and holds. It overlaps the
          outline's left stroke deliberately, which is what makes it read as part
          of the card rather than a thing sitting on top of one. */}
      <rect x="2.5" y="6" width="5.5" height="12" rx="1.6" fill={STRIP_TAB} />
      {detailed ? (
        <path
          d="M11 11h7.5M11 14.5h4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
        />
      ) : (
        <path
          d="M12 12h6"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.6}
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

/** Mark in its tile, plus the wordmark. The tile is a flat fill — it carried a
 *  blue-into-purple gradient until 16 August, and public/favicon.svg carried the
 *  same one for a day longer because it is a separate file. */
export default function Logo({ tile = 26, mark = 16 }: { tile?: number; mark?: number }) {
  return (
    <>
      <span className="brand-mark" style={{ width: tile, height: tile }}>
        <LogoMark size={mark} />
      </span>
      <span className="brand-word">FlightDeck</span>
    </>
  );
}
